import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ContactRound, CalendarClock, MessageCircle } from "lucide-react";
import { fmt, STATUS_LABELS } from "@/pages/admin/utils/format";
import { InvoiceCell, AdminSearchBar, PaginationFooter, LoyalBadge } from "@/pages/admin/components/common";
import { whatsappUrl } from "@/lib/phone";

const BookingsSection = ({
  bookings,
  bookingsInput,
  setBookingsInput,
  bookingsMeta,
  bookingsStatus,
  setBookingsStatus,
  bookingsStatusCounts,
  bookingsPage,
  setBookingsPage,
  invoiceByBooking,
  openClientProfile,
  openReschedule,
  updateBooking,
}) => (
  <>
            <AdminSearchBar
              value={bookingsInput}
              onChange={setBookingsInput}
              placeholder="ابحث بالاسم، الإيميل، رقم الجوال، أو رقم الطلب..."
              testId="admin-bookings-search"
              resultCount={bookingsMeta.total}
            />
            <div
              className="flex flex-wrap gap-2 mb-3"
              data-testid="admin-bookings-status-filters"
              role="tablist"
              aria-label="تصفية الحجوزات حسب الحالة"
            >
              {[
                { key: "all", label: "الكل" },
                { key: "confirmed", label: "المؤكدة" },
                { key: "pending", label: "المعلقة" },
                { key: "canceled", label: "الملغاة" },
                { key: "completed", label: "المنتهية" },
                { key: "failed", label: "الفاشلة" },
              ].map((f) => {
                const active = bookingsStatus === f.key;
                const count = bookingsStatusCounts[f.key];
                return (
                  <button
                    key={f.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    data-testid={`admin-bookings-filter-${f.key}`}
                    onClick={() => setBookingsStatus(f.key)}
                    className={
                      "rounded-full px-4 h-8 text-xs font-medium border transition " +
                      (active
                        ? "bg-[#0A4D3C] text-white border-[#0A4D3C]"
                        : "bg-white text-[#222222] border-[#E5E1D8] hover:bg-[#F6F4EE]")
                    }
                  >
                    {f.label}
                    {typeof count === "number" && (
                      <span className={"ms-1 " + (active ? "text-white/80" : "text-[#5E5C58]")}>
                        ({count.toLocaleString("ar-SA")})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="card-warm p-0 overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-bookings-table">
                <thead className="bg-[#F6F4EE] text-[#5E5C58]">
                  <tr>
                    <th className="text-start p-4">العميل</th>
                    <th className="text-start p-4">الخدمة</th>
                    <th className="text-start p-4">الموعد</th>
                    <th className="text-start p-4">المبلغ</th>
                    <th className="text-start p-4">الحالة</th>
                    <th className="text-start p-4">الفاتورة</th>
                    <th className="text-start p-4">إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-6 text-center text-[#5E5C58]" data-testid="admin-bookings-empty">
                        {bookingsStatus !== "all" ? "لا توجد حجوزات بهذه الحالة" : "لا توجد حجوزات."}
                      </td>
                    </tr>
                  )}
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-[#E5E1D8]">
                      <td className="p-4">
                        <div className="font-medium flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            data-testid={`admin-bookings-open-client-${b.id}`}
                            onClick={() => openClientProfile(b)}
                            title="فتح ملف العميل"
                            className="inline-flex items-center gap-1 text-[#0A4D3C] hover:underline focus:outline-none focus:ring-1 focus:ring-[#0A4D3C] rounded"
                          >
                            <ContactRound className="w-3.5 h-3.5" />
                            <span>
                              {[b.user_first_name, b.user_middle_name, b.user_last_name].filter(Boolean).join(" ") || b.user_name || "—"}
                            </span>
                          </button>
                          {b.user_is_loyal && <LoyalBadge count={b.user_transaction_count} />}
                        </div>
                        <div className="text-xs text-[#5E5C58]" dir="ltr">{b.user_email}</div>
                        {b.user_phone && (() => {
                          const wa = whatsappUrl(b.user_phone);
                          return wa ? (
                            <a
                              href={wa}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="فتح واتساب"
                              data-testid={`admin-bookings-whatsapp-${b.id}`}
                              className="mt-0.5 text-xs text-[#1ebe5b] hover:underline inline-flex items-center gap-1"
                              dir="ltr"
                            >
                              <MessageCircle className="w-3 h-3" />
                              {b.user_phone}
                            </a>
                          ) : (
                            <div className="text-xs text-[#1ebe5b] mt-0.5" dir="ltr">
                              {b.user_phone}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="p-4">{b.service_title_ar}</td>
                      <td className="p-4">{fmt(b.slot_iso)}</td>
                      <td className="p-4">{Number(b.amount).toLocaleString("ar-SA")} ر.س</td>
                      <td className="p-4">
                        <Badge className="rounded-full bg-stone-100 text-stone-700">
                          {STATUS_LABELS[b.status] || b.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <InvoiceCell invoice={invoiceByBooking[b.id]} />
                      </td>
                      <td className="p-4">
                        {b.status !== "canceled" && b.status !== "completed" && (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              data-testid={`admin-reschedule-booking-${b.id}`}
                              size="sm"
                              variant="outline"
                              className="rounded-full text-[#0A4D3C] border-[#0A4D3C]/40 hover:bg-[#F6F4EE]"
                              onClick={() => openReschedule(b)}
                            >
                              <CalendarClock className="w-3.5 h-3.5 ms-1" />
                              ترحيل
                            </Button>
                            <Button
                              data-testid={`admin-mark-completed-${b.id}`}
                              size="sm"
                              variant="outline"
                              className="rounded-full"
                              onClick={() => updateBooking(b.id, "completed")}
                            >
                              منتهية
                            </Button>
                            <Button
                              data-testid={`admin-cancel-booking-${b.id}`}
                              size="sm"
                              variant="outline"
                              className="rounded-full"
                              onClick={() => updateBooking(b.id, "canceled")}
                            >
                              إلغاء
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationFooter
              page={bookingsPage}
              totalPages={bookingsMeta.total_pages}
              total={bookingsMeta.total}
              onPrev={() => setBookingsPage((p) => Math.max(1, p - 1))}
              onNext={() => setBookingsPage((p) => Math.min(bookingsMeta.total_pages || 1, p + 1))}
              testId="admin-bookings-pager"
            />
  </>
);

export { BookingsSection };
